"use client";
import React from "react";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function AllInOnePOSSection({ section }) {
  const title = section?.title || "All-in-one POS";
  const items = section?.items || [
    {
      bold: "Customizable products.",
      text: " Customers can buy the appropriate items and customize the prescription strength, product color, or additional options."
    },
    {
      bold: "Barcode scanning.",
      text: " Efficiently scan products to speed up checkout and reduce errors."
    },
    {
      bold: "Create promotions and combos.",
      text: " Use promotion strategies, loyalty points, and discounts, or create combos to boost sales."
    }
  ];
  const image = section?.image || "/odoo/industries/arts-crafts/pos.webp";

  return (
    <section className="py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          {/* Left Column: Text & Features */}
          <div className="lg:col-span-5 space-y-8 z-10">
            {/* Title with decorative underline */}
            <div className="relative inline-block">
              <h2 className={`${caveat.className} text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight text-white flex items-center flex-wrap gap-2`}>
                <span className="relative inline-block pb-2">
                  {title.split(" ")[0]}
                  <svg className="absolute bottom-0 left-0 w-full h-3.5 text-sky-400" viewBox="0 0 200 20" fill="none" preserveAspectRatio="none">
                    <path d="M5 12 Q 50 2, 100 12 T 195 12" stroke="currentColor" strokeWidth="6" strokeLinecap="round"/>
                  </svg>
                </span>
                <span className="text-white ml-2 font-sans font-extrabold tracking-normal">
                  {title.split(" ").slice(1).join(" ")}
                </span>
              </h2>
            </div>

            {/* Feature Bullet Points */}
            <div className="space-y-6 text-slate-300 text-lg md:text-xl leading-relaxed">
              {items.map((item, idx) => (
                <p key={idx}>
                  <strong className="text-white font-semibold">{item.bold}</strong>
                  {item.text}
                </p>
              ))}
            </div>
          </div>

          {/* Right Column: POS Screenshot with Large Light Circle Backdrop */}
          <div className="lg:col-span-7 relative flex justify-center items-center py-4">
            {/* Giant Circular Backdrop */}
            <div className="absolute w-[440px] h-[440px] sm:w-[540px] sm:h-[540px] lg:w-[600px] lg:h-[600px] rounded-full bg-white/10 shadow-inner pointer-events-none -z-10" />

            {/* Main Mockup Container */}
            <div className="relative rounded-2xl bg-white p-2.5 sm:p-3.5 shadow-2xl border border-white/20 max-w-full z-10">
              <Image
                src={image}
                width={750}
                height={480}
                alt="All-in-one POS Interface"
                className="rounded-xl w-full h-auto object-cover"
                priority
              />

              {/* Top-Right Hand-Drawn Blue Burst Accent Lines Icon */}
              <div className="absolute -top-10 -right-6 w-[69px] h-[68px] text-sky-400 pointer-events-none select-none z-30">
                <svg
                  viewBox="0 0 60 60"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  className="w-full h-full"
                >
                  <line x1="18" y1="18" x2="26" y2="26" />
                  <line x1="31" y1="9" x2="33" y2="23" />
                  <line x1="42" y1="24" x2="23" y2="23" />
                  <line x1="42" y1="36" x2="54" y2="42" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
